import type { BulkSignoffResult, ReviewerLoad, TierFit } from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { ENDPOINTS } from './endpoints';

export interface QueueCounters {
  awaiting: number;
  inProgress: number;
  openFlags: number;
  signedOff: number;
  openConvs: number;
  clients: number;
}

export interface QueueRow {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  tier: string;
  /** Current-period close id (null when the org has no close this month). */
  closeId: string | null;
  periodStatus: string | null;
  timeSpentMinutes: number | null;
  flagCount: number;
  openConvCount: number;
  /** B3 tier-fit signal (GOOD / WATCH / OVER_TIER + reasons). */
  tierFit: TierFit;
  reviewer: { id: string; fullName: string | null } | null;
}

export interface QueueSummary {
  counters: QueueCounters;
  /** B1 capacity of the caller (the "my load" chip). */
  myLoad: ReviewerLoad;
  rows: QueueRow[];
}

export async function getQueueSummary(mine = false): Promise<QueueSummary> {
  const suffix = mine ? '?mine=true' : '';
  const res = await apiClient.get<QueueSummary>(`${ENDPOINTS.queue.summary}${suffix}`);
  return res.data;
}

export interface AssignReviewerResult {
  close: { id: string; organizationId: string; reviewerId: string | null; status: string };
  assignee: { id: string; load: ReviewerLoad };
}

/** Assign (or claim) a client's current-period close to a reviewer (B1). */
export async function assignReviewer(input: {
  organizationId: string;
  reviewerId?: string;
  force?: boolean;
}): Promise<AssignReviewerResult> {
  const res = await apiClient.post<AssignReviewerResult>(ENDPOINTS.queue.assign, input);
  return res.data;
}

/** Cross-client bulk sign-off of review-ready closes (B5). */
export async function bulkSignoff(closeIds: string[]): Promise<BulkSignoffResult> {
  const res = await apiClient.post<BulkSignoffResult>(ENDPOINTS.monthlyClose.bulkSignoff, {
    closeIds,
  });
  return res.data;
}
